import React from "react";
import { Check as CheckIcon, Copy, Eye, Lock, Pencil, Plus } from "lucide-react";
import { VisuallyHidden } from "../../atoms/VisuallyHidden";
import {
  ActionButton,
  Actions,
  AddIcon,
  AddTile,
  Badge,
  Body,
  Card,
  CardIcon,
  CardText,
  Cards,
  Check,
  Description,
  ErrorText,
  Fieldset,
  Gallery,
  GalleryCard,
  Hint,
  Legend,
  LegendDetail,
  Media,
  Meta,
  More,
  Pick,
  Required,
  Swatch,
  Swatches,
  Tag,
  Thumbs,
  TileCard,
  TileCards,
  TileCheck,
  TitleRow,
  Visual,
} from "./RadioCardGroup.styles";
import type { RadioCardAction, RadioCardGroupProps, RadioCardOption } from "./RadioCardGroup.types";

const ACTION_ICON: Record<NonNullable<RadioCardAction["icon"]>, React.ReactNode> = {
  preview: <Eye size={12} aria-hidden="true" />,
  copy: <Copy size={12} aria-hidden="true" />,
  edit: <Pencil size={12} aria-hidden="true" />,
};

/** The ids a radio is described by (its detail line, the group's error), or undefined when none. */
const describedBy = (...ids: (string | null)[]): string | undefined => ids.filter(Boolean).join(" ") || undefined;

const GalleryOption: React.FC<{
  name: string;
  option: RadioCardOption;
  checked: boolean;
  required: boolean;
  /** The group's error id while it shows one. */
  errorId: string | null;
  onChange: (value: string) => void;
}> = ({ name, option, checked, required, errorId, onChange }) => {
  const id = `${name}-${option.value}`;
  const items = option.media?.items ?? [];
  return (
    <GalleryCard $checked={checked} $disabled={Boolean(option.disabled)} data-checked={checked || undefined}>
      <Pick htmlFor={id} $disabled={Boolean(option.disabled)}>
        {items.length ? (
          <Media aria-hidden="true">
            <Thumbs $fit={option.media?.fit ?? (items.length === 1 ? "page" : "row")} data-fit={option.media?.fit ?? (items.length === 1 ? "page" : "row")}>
              {items.map((item) => (
                <span key={item.key}>{item.content}</span>
              ))}
            </Thumbs>
            {option.media?.more ? <More>{option.media.more}</More> : null}
          </Media>
        ) : null}
        <Body>
          <TitleRow>
            <input
              type="radio"
              id={id}
              name={name}
              value={option.value}
              checked={checked}
              disabled={option.disabled}
              required={required}
              aria-labelledby={`${id}-title`}
              aria-describedby={describedBy(option.description ? `${id}-detail` : null, errorId)}
              onChange={() => onChange(option.value)}
            />
            <b id={`${id}-title`}>{option.title}</b>
            {option.badge ? (
              <Badge aria-hidden="true">
                <CheckIcon size={12} />
                {option.badge}
              </Badge>
            ) : null}
          </TitleRow>
          {option.description ? <Description id={`${id}-detail`}>{option.description}</Description> : null}
          {option.swatches?.length || option.tags?.length ? (
            <Meta>
              {option.swatches?.length ? (
                <Swatches aria-hidden="true">
                  {option.swatches.map((swatch) => (
                    <Swatch key={swatch.key} title={swatch.label} style={{ background: swatch.color }} />
                  ))}
                </Swatches>
              ) : null}
              {option.tags?.map((tag) => (
                <Tag key={tag.key} $tone={tag.tone ?? "neutral"}>
                  {tag.locked ? <Lock size={10} aria-hidden="true" /> : null}
                  {tag.label}
                </Tag>
              ))}
            </Meta>
          ) : null}
        </Body>
      </Pick>
      {option.actions?.length ? (
        <Actions>
          {option.actions.map((action) => (
            <ActionButton key={action.key} type="button" id={action.id} onClick={action.onClick}>
              {action.icon ? ACTION_ICON[action.icon] : null}
              {action.label}
              {action.srSuffix ? <VisuallyHidden>{action.srSuffix}</VisuallyHidden> : null}
            </ActionButton>
          ))}
        </Actions>
      ) : null}
    </GalleryCard>
  );
};

/**
 * A single choice as cards, each with a title, a line of detail, an optional visual and icon;
 * nothing chosen while `value` is null. The tiles variant draws small cards with the visual on top;
 * the gallery variant shows picture cards with their own buttons, and an add tile. In every
 * variant, an error outlines the cards and describes every radio, and `trailing` closes the row.
 */
export const RadioCardGroup: React.FC<RadioCardGroupProps> = ({
  name,
  legend,
  legendDetail = null,
  required = false,
  hint = null,
  options,
  value,
  onChange,
  errorMessage = null,
  variant = "list",
  addTile = null,
  trailing = null,
  columns,
  appearance = "card",
  className,
}) => (
  <Fieldset className={className} aria-describedby={hint ? `${name}-hint` : undefined}>
    <Legend $hidden={appearance === "plain"}>
      {legend}
      {required ? <Required aria-hidden="true"> *</Required> : null}
      {legendDetail ? <LegendDetail> {legendDetail}</LegendDetail> : null}
    </Legend>
    {hint ? <Hint id={`${name}-hint`}>{hint}</Hint> : null}
    {variant === "gallery" ? (
      <Gallery $invalid={Boolean(errorMessage)}>
        {options.map((option) => (
          <GalleryOption
            key={option.value}
            name={name}
            option={option}
            checked={option.value === value}
            required={required}
            errorId={errorMessage ? `${name}-error` : null}
            onChange={onChange}
          />
        ))}
        {addTile ? (
          <AddTile type="button" id={addTile.id} onClick={addTile.onClick}>
            <AddIcon aria-hidden="true">
              <Plus size={18} />
            </AddIcon>
            <b>{addTile.title}</b>
            {addTile.description ? <span>{addTile.description}</span> : null}
          </AddTile>
        ) : null}
        {trailing}
      </Gallery>
    ) : variant === "tiles" ? (
      <TileCards $invalid={Boolean(errorMessage)} data-variant="tiles">
        {options.map((option) => (
          <TileCard key={option.value} $checked={option.value === value}>
            <TileCheck
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.disabled}
              required={required}
              aria-labelledby={`${name}-${option.value}-title`}
              aria-describedby={describedBy(
                option.description ? `${name}-${option.value}-detail` : null,
                errorMessage ? `${name}-error` : null,
              )}
              onChange={() => onChange(option.value)}
            />
            {option.visual ? <Visual>{option.visual}</Visual> : null}
            <CardText>
              <b id={`${name}-${option.value}-title`}>{option.title}</b>
              {option.description ? <span id={`${name}-${option.value}-detail`}>{option.description}</span> : null}
            </CardText>
          </TileCard>
        ))}
        {trailing}
      </TileCards>
    ) : (
      <Cards $invalid={Boolean(errorMessage)} $columns={columns} $plain={appearance === "plain"}>
        {options.map((option) => (
          <Card key={option.value} $checked={option.value === value}>
            <Check
              type="radio"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.disabled}
              required={required}
              aria-labelledby={`${name}-${option.value}-title`}
              aria-describedby={describedBy(
                option.description ? `${name}-${option.value}-detail` : null,
                errorMessage ? `${name}-error` : null,
              )}
              onChange={() => onChange(option.value)}
            />
            <CardText>
              <b id={`${name}-${option.value}-title`}>{option.title}</b>
              {option.visual ? <Visual>{option.visual}</Visual> : null}
              {option.description ? <span id={`${name}-${option.value}-detail`}>{option.description}</span> : null}
            </CardText>
            {option.icon && appearance !== "plain" ? <CardIcon aria-hidden="true">{option.icon}</CardIcon> : null}
          </Card>
        ))}
        {trailing}
      </Cards>
    )}
    {errorMessage ? (
      <ErrorText id={`${name}-error`} role="alert">
        {errorMessage}
      </ErrorText>
    ) : null}
  </Fieldset>
);

export default RadioCardGroup;
